import { Moment } from 'moment';
import { Module } from 'vuex';
import ContactContext from '@/contact/models/ContactContext';
import DossierContactService from '@/contact/services/DossierContactService';
import DossierClient from '@/dossier/client/DossierClient';
import { DossierId } from '@/dossier/models/AbstractDossier';
import { DossierDetailsState, RootState } from '@/general/store/states';
import DossierContactTarget from '@/shared/models/DossierContactTarget';
import { present } from '@/shared/utils/typescript';
import WorkshopPlanningClient from '../client/WorkshopPlanningClient';
import DossierDetails from '../models/DossierDetails';
import PlannerDossierAdapter from '../models/PlannerDossierAdapter';

export interface ConfirmationPayload {
  appointment: Moment;
  preferredPickUp: Moment;
  targets: Array<DossierContactTarget>
}

interface UpdateAppointmentPayload {
  dossierId: DossierId;
  waitsOnPremise: boolean;
  preferredPickUp: Moment;
}

const module: Module<DossierDetailsState, RootState> = {
  namespaced: true,

  state: {
    current: null,
    contacts: null,
  },

  mutations: {
    setCurrent(state, details: DossierDetails) {
      state.current = details;
    },

    setContacts(state, contacts: ContactContext) {
      state.contacts = contacts;
    },

    updateConfirmationTimings({ current }, { appointment, preferredPickUp }: ConfirmationPayload) {
      if (current) {
        current.appointment = appointment;
        current.preferredPickUp = preferredPickUp;
      }
    },

    updateAppointment({ current }, { dossierId, waitsOnPremise, preferredPickUp }: UpdateAppointmentPayload) {
      if (current?.id === dossierId) {
        current.waitsOnPremise = waitsOnPremise;
        current.preferredPickUp = preferredPickUp;
      }
    },

    clear(state) {
      state.current = null;
      state.contacts = null;
    },
  },

  actions: {
    async load({ commit }, dossierId: DossierId): Promise<void> {
      const [details, contactContext] = await Promise.all([
        WorkshopPlanningClient.loadDossierDetails(dossierId),
        DossierContactService.loadAllForDossier(dossierId),
      ]);
      commit('setCurrent', details);
      commit('setContacts', contactContext);
    },

    async asDossier({ state }): Promise<PlannerDossierAdapter> {
      const details = present(state.current);
      const dossier = await DossierClient.findById(present(details.id));
      return new PlannerDossierAdapter(details, dossier);
    },
  },
};

export default module;
